/**
 * SendButton 组件
 * 聊天输入区域的发送按钮，根据输入状态显示不同样式
 */
import React, { memo, useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { hasContentAtom, sttProcessingAtom, isVoiceRecordingAtom } from '../../../../atoms/v3/chatInputAtoms';
import { messageSendingAtom } from '../../../../atoms/v3/chatAtoms';
import { triggerChatMessageAtom } from '../../../../atoms/v3/tirggerChatMessageAtom';
import { isUploadingAtom} from '../../../../atoms/v3/uppyAtoms';

// shouldAutoScrollAtom 已在 triggerChatMessageAtom 中处理，不需要在这里导入

interface SendButtonProps {
  isDisabled?: boolean;
  className?: string;
  quickbarMode?: boolean;
  onNewTask?: () => void;
  onNewProject?: () => void;
}

const SendButtonComp: React.FC<SendButtonProps> = ({
  isDisabled = false,
  className = '',
  quickbarMode = false,
  onNewTask,
  onNewProject
}) => {
  const hasContent = useAtomValue(hasContentAtom);
  const isSending = useAtomValue(messageSendingAtom);
  const isUploading = useAtomValue(isUploadingAtom);
  // 🔥 获取录音和STT相关状态
  const isSTTProcessing = useAtomValue(sttProcessingAtom);
  const isVoiceRecording = useAtomValue(isVoiceRecordingAtom);
  const sendChatMessage = useSetAtom(triggerChatMessageAtom);

  // 处理发送消息 - 使用useCallback优化
  const handleSend = useCallback(async () => {
    // quickbar 模式下执行对应的新建操作
    if (quickbarMode) {
      if (onNewProject) {
        onNewProject();
      } else if (onNewTask) {
        onNewTask();
      }
      return;
    }

    // 🔥 如果正在发送消息、上传文件、录音或STT处理，则不允许发送
    if (isSending || isUploading || isVoiceRecording || isSTTProcessing) {
      const reason = isSending ? '正在发送消息' :
                    isUploading ? '文件正在上传' :
                    isVoiceRecording ? '正在录音' :
                    '正在转录';
      console.log('消息发送被阻止：', reason);
      return;
    }
    console.log('Send button clicked, enabling auto-scroll');

    await sendChatMessage();
  }, [isSending, isUploading, isVoiceRecording, isSTTProcessing, sendChatMessage, quickbarMode, onNewTask, onNewProject]);

  // quickbar 模式下的按钮样式和行为
  if (quickbarMode) {
    return (
      <>
        {/* 新建项目按钮 */}
        {onNewProject && (
          <button
            onClick={() => onNewProject()}
            className="flex-shrink-0 w-10 h-10 bg-gray-500 rounded-full flex items-center justify-center hover:bg-gray-600 transition-colors ml-2 mr-2"
            title="新建项目"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11v4m-2-2h4" />
            </svg>
          </button>
        )}

        {/* 新建任务按钮 */}
        <button
          onClick={() => onNewTask?.()}
          className={`flex-shrink-0 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors ${onNewProject ? '' : 'ml-2'}`}
          title="新建任务"
        >
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </>
    );
  }

  return (
    <button
      onClick={handleSend}
      disabled={isSending || isDisabled || isUploading || isVoiceRecording || isSTTProcessing || !hasContent}
      className={`flex-shrink-0 ml-2 transition-opacity duration-200 ease-in-out relative z-50 ${
        hasContent && !isVoiceRecording && !isSTTProcessing ? 'opacity-100' : 'opacity-50'
      } ${className}`}
      aria-label="发送"
    >
      {isSending || isUploading || isVoiceRecording || isSTTProcessing ? (
        <div className="relative w-8 h-8 flex items-center justify-center">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-ios-blue/30 dark:border-ios-dark-blue/30 rounded-full"></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-5 h-5 border-t-2 border-ios-blue dark:border-ios-dark-blue rounded-full animate-ios-spin"></div>
          </div>
        </div>
      ) : (
        <div className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors duration-200 ease-in-out ${
          hasContent
            ? 'bg-blue-500 hover:bg-blue-600 text-white'
            : 'bg-ios-gray4 dark:bg-ios-dark-gray3 text-ios-gray dark:text-ios-dark-gray'
        }`}>
          {/* Chat 模式下使用纸飞机图标 */}
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22,2 15,22 11,13 2,9 22,2"/>
          </svg>
        </div>
      )}
    </button>
  );
};

export const SendButton = memo(SendButtonComp);
export default SendButton;
